/** @import {NationalInsuranceNumberProps} from './NationalInsuranceNumberTypes' */
/** @import {CustomVerification} from './OTPModalTypes' */

import React, { useState, useEffect } from 'react';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import TextInput from '../Form/TextInput/TextInput';
import Section from './Section';
import OTPModal from './OTPModal';
import { nationalInsuranceTest, nationalInsuranceCompleteTest } from '../../utils/inputValidations';

function NationalInsuranceNumberOTPModal(/** @type {{ nationalInsuranceNumber: string; mobileNumber: string; onClose: () => void; onComplete: () => void; verificationToken: string; customVerification: CustomVerification; }} */ { nationalInsuranceNumber, mobileNumber, onClose, onComplete, verificationToken, customVerification }) {
    return (
        <OTPModal
            title="Update National Insurance number"
            mobileNumber={mobileNumber}
            successMessage="Your National Insurance number has been updated."
            onClose={onClose}
            onComplete={onComplete}
            verificationToken={verificationToken}
            customVerification={customVerification}
            isResendActivationOtp={true}
            analyticsRegion={"my profile"}
            analyticsAction={"National Insurance number change"}
        />
    );
}

const SEND_OTP = gql`
  mutation SendLoggedInOtp($request : SendOtpRequestType!){
    sendLoggedInOtp(request:$request) {
      success
      verificationToken
    }
  }  
`;

const UPDATE_NATIONAL_INSURANCE_NUMBER = gql`
  mutation updateNationalInsuranceNumber($request : UpdateNationalInsuranceNumberInputType!){
    updateNationalInsuranceNumber(request:$request) {
      success
    }
  }  
`;

function NationalInsuranceNumber(/** @type {NationalInsuranceNumberProps} */ { profile, onSave }) {
    const [editing, setEditing] = useState(false);
    const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState(profile.nationalInsuranceNumber || '');
    const [otpModalOpen, setOtpModalOpen] = useState(false);
    const [sendOtp] = useMutation(SEND_OTP);
    const [updateNIN] = useMutation(UPDATE_NATIONAL_INSURANCE_NUMBER);
    const [verificationToken, setVerificationToken] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [validationErrorLabel, setValidationErrorLabel] = useState('');
    const [invalidOtpError, setInvalidOtpError] = useState(false);
    const [unexpectedError, setUnexpectedError] = useState(false);

    const cancelEditing = () => {
        setNationalInsuranceNumber(profile.nationalInsuranceNumber || '');
        setShowValidation(false);
    };

    const startEditing = (/** @type {boolean} */ editing) => {
        if (editing) {
            setNationalInsuranceNumber('');
        }
        setEditing(editing);
    };

    const setNINChange = (/** @type {string} */ value) => {
        // Remove spaces and convert to uppercase
        const cleaned = value.replace(/\s/g, '').toUpperCase();
        // Limit to 9 characters
        const limited = cleaned.length > 9 ? cleaned.slice(0, 9) : cleaned;
        setNationalInsuranceNumber(limited);

        // Show validation error in real-time if invalid characters are entered
        if (limited && !nationalInsuranceTest(limited)) {
            setShowValidation(true);
            setValidationErrorLabel("Please check you've entered a valid NI number and try again");
        } else {
            setShowValidation(false);
        }
    };

    const validateNIN = (/** @type {string} */ value) => {
        return nationalInsuranceTest(value);
    };

    const ninValidation = () => {
        if (nationalInsuranceNumber) {
            if (!nationalInsuranceCompleteTest(nationalInsuranceNumber)) {
                setShowValidation(true);
                setValidationErrorLabel("Please check you've entered a valid NI number and try again");
                return false;
            }
            return true;
        }
        setShowValidation(true);
        setValidationErrorLabel('National Insurance number is required');
        return false;
    };

    const approveEditing = async () => {
        if (!ninValidation()) {
            return;
        }
        // Open modal first (matches MobileNumber flow), then request OTP
        setOtpModalOpen(true);

        const result = await sendOtp({
            variables: {
                request: {}
            }
        });

        if (result?.data?.sendLoggedInOtp?.verificationToken) {
            setVerificationToken(result.data.sendLoggedInOtp.verificationToken);
        }
    };

    const onModalCancel = () => {
        setOtpModalOpen(false);
        cancelEditing();
    };

    const onModalComplete = () => {
        setOtpModalOpen(false);
        setEditing(false);
        onSave && onSave({ nationalInsuranceNumber });
    };

    /** @type {CustomVerification} */
    const customVerification = async (/** @type {unknown} */ otp) => {
        // Reset error states before attempting verification
        setInvalidOtpError(false);
        setUnexpectedError(false);

        try {
            const result = await updateNIN({
                variables: {
                    request: {
                        nationalInsuranceNumber: nationalInsuranceNumber.replace(/\s/g, ''),
                        activateOtp: {
                            passCode: otp,
                            verificationToken: verificationToken
                        }
                    }
                }
            });

            const success = result?.data?.updateNationalInsuranceNumber?.success === true;

            return {
                success: success,
                data: { approved: success }
            };
        }
        catch (e) {
            if (e instanceof Error && e.message.includes("InvalidOtpError")) {
                setInvalidOtpError(true);
            }
            else {
                setUnexpectedError(true);
            }

            return {
                success: false,
                data: { approved: false }
            };
        }
    };

    const formatDisplayNIN = (/** @type {string} */ value) => {
        if (!value) return '';
        const cleaned = value.replace(/\s/g, '');
        return cleaned.replace(/(.{2})(.{2})(.{2})(.{2})(.{1})/, '$1 $2 $3 $4 $5').trim();
    };

    useEffect(() => {
        setNationalInsuranceNumber(profile.nationalInsuranceNumber || '');
    }, [profile.nationalInsuranceNumber]);

    return (
        <Section
            title="National Insurance number"
            editing={editing}
            setEditing={startEditing}
            onCancel={cancelEditing}
            onSave={() => { void approveEditing(); }} // Void to indicate we're intentionally ignoring the promise
            validation={ninValidation}
            valid={editing ? validateNIN(nationalInsuranceNumber) : true}
        >
            <TextInput
                value={editing ? nationalInsuranceNumber : formatDisplayNIN(nationalInsuranceNumber)}
                onChange={setNINChange}
                disabled={!editing}
                hasError={showValidation}
            />
            {otpModalOpen && <NationalInsuranceNumberOTPModal
                nationalInsuranceNumber={nationalInsuranceNumber}
                mobileNumber={profile.mobileNumber}
                onClose={onModalCancel}
                onComplete={onModalComplete}
                verificationToken={verificationToken}
                customVerification={customVerification} />}
            <div className={showValidation && unexpectedError ? "login__error" : "login__error login__error--hide"}>
                <span className="login__error--wrap">An unexpected error occurred, please try again later.</span>
            </div>
            <div className={showValidation && invalidOtpError ? "login__error" : "login__error login__error--hide"}>
                <span className="login__error--wrap">Your code has either expired or been entered incorrectly. Please try again or click below to resend a new code.</span>
            </div>
            <div className={showValidation && !invalidOtpError && !unexpectedError ? 'login__error' : 'login__error login__error--hide'}>
                <span className="login__error--wrap">{validationErrorLabel}</span>
            </div>
        </Section>
    );
}

export default NationalInsuranceNumber;
